import { Component, OnInit, Output, EventEmitter, Input } from '@angular/core';
import { trigger, state, style, transition, animate } from '@angular/animations';

interface CalendarDay {
  day: number;
  date: Date;
  isCurrentMonth: boolean;
}

@Component({
  selector: 'app-date-range-modal',
  templateUrl: './date-range-modal.component.html',
  styleUrls: ['./date-range-modal.component.scss'],
  animations: [
    trigger('fadeIn', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('250ms ease-in', style({ opacity: 1 }))
      ]),
      transition(':leave', [
        animate('200ms ease-out', style({ opacity: 0 }))
      ])
    ])
  ]
})
export class DateRangeModalComponent implements OnInit {
  @Input() isOpen = false;
  @Input() minDate: Date = new Date(1825, 0, 1); // Inicio del archivo histórico
  @Input() maxDate: Date = new Date(2025, 11, 31);

  @Output() dateRangeSelected = new EventEmitter<{ start: Date, end: Date }>();
  @Output() closeEvent = new EventEmitter<void>();

  startDate: Date | null = null;
  endDate: Date | null = null;

  startMonth: number;
  startYear: number;
  endMonth: number;
  endYear: number;

  // Variable para rastrear el botón de rango rápido activo
  activeQuickRange: 'siglo19' | 'siglo20' | 'siglo21' | 'bicentenario' | null = null;

  // Variables para guardar valores previos en caso de input inválido
  previousStartYear: number;
  previousEndYear: number;

  startCalendarDays: CalendarDay[] = [];
  endCalendarDays: CalendarDay[] = [];

  activeTab: 'start' | 'end' = 'start';

  weekDays = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
  months = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];
  years: number[] = []; // Ya no se usa, pero lo dejamos por compatibilidad

  constructor() {
    const today = new Date();
    this.startMonth = today.getMonth();
    this.startYear = today.getFullYear();
    this.endMonth = today.getMonth();
    this.endYear = today.getFullYear();

    // Inicializar valores previos
    this.previousStartYear = this.startYear;
    this.previousEndYear = this.endYear;
  }

  ngOnInit(): void {
    // Generar array de años desde minDate hasta maxDate
    const startYear = this.minDate.getFullYear();
    const endYear = this.maxDate.getFullYear();
    for (let year = startYear; year <= endYear; year++) {
      this.years.push(year);
    }

    this.generateStartCalendar();
    this.generateEndCalendar();
  }

  generateStartCalendar(): void {
    this.startCalendarDays = this.generateCalendarDays(this.startYear, this.startMonth);
  }

  generateEndCalendar(): void {
    this.endCalendarDays = this.generateCalendarDays(this.endYear, this.endMonth);
  }

  generateCalendarDays(year: number, month: number): CalendarDay[] {
    const days: CalendarDay[] = [];
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDayOfWeek = firstDay.getDay();
    const daysInMonth = lastDay.getDate();

    // Días del mes anterior
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = startDayOfWeek - 1; i >= 0; i--) {
      const day = prevMonthLastDay - i;
      const date = new Date(year, month - 1, day);
      days.push({ day, date, isCurrentMonth: false });
    }

    // Días del mes actual
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      days.push({ day, date, isCurrentMonth: true });
    }

    // Días del mes siguiente para completar la grilla
    const remainingDays = 42 - days.length; // 6 semanas x 7 días
    for (let day = 1; day <= remainingDays; day++) {
      const date = new Date(year, month + 1, day);
      days.push({ day, date, isCurrentMonth: false });
    }

    return days;
  }

  onStartMonthYearChange(): void {
    this.generateStartCalendar();
  }

  onEndMonthYearChange(): void {
    this.generateEndCalendar();
  }

  selectStartDate(day: CalendarDay): void {
    if (this.isStartDateDisabled(day)) return;

    // ✅ Limpiar el rango rápido activo cuando se selecciona manualmente
    this.activeQuickRange = null;

    // ✅ Crear fecha LOCAL copiando año, mes y día (sin zona horaria)
    this.startDate = new Date(day.date.getFullYear(), day.date.getMonth(), day.date.getDate());
    // console.log('🗓️ Fecha de inicio seleccionada:', this.startDate.toString());

    // Si la fecha fin es anterior a la fecha inicio, limpiar fecha fin
    if (this.endDate && this.endDate < this.startDate) {
      this.endDate = null;
    }

    // Cambiar automáticamente al tab de fecha fin
    this.activeTab = 'end';
    // console.log('🔄 Cambiando automáticamente al tab de Fecha Fin');
  }

  selectEndDate(day: CalendarDay): void {
    if (this.isEndDateDisabled(day)) return;

    // ✅ Limpiar el rango rápido activo cuando se selecciona manualmente
    this.activeQuickRange = null;

    // ✅ Crear fecha LOCAL copiando año, mes y día (sin zona horaria)
    this.endDate = new Date(day.date.getFullYear(), day.date.getMonth(), day.date.getDate());
    // console.log('🗓️ Fecha de fin seleccionada:', this.endDate.toString());

    // Si la fecha inicio es posterior a la fecha fin, limpiar fecha inicio
    if (this.startDate && this.startDate > this.endDate) {
      this.startDate = null;
    }
  }

  isStartDateSelected(day: CalendarDay): boolean {
    if (!this.startDate || !day.isCurrentMonth) return false;
    return this.isSameDay(day.date, this.startDate);
  }

  isEndDateSelected(day: CalendarDay): boolean {
    if (!this.endDate || !day.isCurrentMonth) return false;
    return this.isSameDay(day.date, this.endDate);
  }

  isDateInRange(day: CalendarDay): boolean {
    if (!this.startDate || !this.endDate || !day.isCurrentMonth) return false;

    // ✅ Comparar solo año/mes/día (ignorar hora)
    const dayTime = new Date(day.date.getFullYear(), day.date.getMonth(), day.date.getDate()).getTime();
    const startTime = new Date(this.startDate.getFullYear(), this.startDate.getMonth(), this.startDate.getDate()).getTime();
    const endTime = new Date(this.endDate.getFullYear(), this.endDate.getMonth(), this.endDate.getDate()).getTime();

    return dayTime > startTime && dayTime < endTime;
  }

  isStartDateDisabled(day: CalendarDay): boolean {
    // ✅ Comparar solo año/mes/día (ignorar hora)
    const dayTime = new Date(day.date.getFullYear(), day.date.getMonth(), day.date.getDate()).getTime();
    const minTime = new Date(this.minDate.getFullYear(), this.minDate.getMonth(), this.minDate.getDate()).getTime();
    const maxTime = new Date(this.maxDate.getFullYear(), this.maxDate.getMonth(), this.maxDate.getDate()).getTime();

    return dayTime < minTime || dayTime > maxTime;
  }

  isEndDateDisabled(day: CalendarDay): boolean {
    // ✅ Comparar solo año/mes/día (ignorar hora)
    const dayTime = new Date(day.date.getFullYear(), day.date.getMonth(), day.date.getDate()).getTime();
    const minTime = new Date(this.minDate.getFullYear(), this.minDate.getMonth(), this.minDate.getDate()).getTime();
    const maxTime = new Date(this.maxDate.getFullYear(), this.maxDate.getMonth(), this.maxDate.getDate()).getTime();

    if (dayTime < minTime || dayTime > maxTime) return true;

    // La fecha fin no puede ser anterior a la fecha inicio
    if (this.startDate) {
      const startTime = new Date(this.startDate.getFullYear(), this.startDate.getMonth(), this.startDate.getDate()).getTime();
      if (dayTime < startTime) return true;
    }

    return false;
  }

  isSameDay(date1: Date, date2: Date): boolean {
    return date1.getFullYear() === date2.getFullYear() &&
           date1.getMonth() === date2.getMonth() &&
           date1.getDate() === date2.getDate();
  }

  setQuickRange(range: 'siglo19' | 'siglo20' | 'siglo21' | 'bicentenario'): void {
    // ✅ Marcar el botón activo
    this.activeQuickRange = range;

    // ✅ Obtener fecha actual (solo año, mes, día - sin hora)
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth();
    const currentDay = today.getDate();

    switch (range) {
      case 'siglo19':
        // Siglo XIX: 1825-1899 (inicio del archivo histórico)
        this.startDate = new Date(1825, 0, 1);  // 01/01/1825
        this.endDate = new Date(1899, 11, 31);  // 31/12/1899
        // console.log('📅 Rango rápido Siglo XIX:', this.startDate.toString(), '-', this.endDate.toString());
        break;

      case 'siglo20':
        // Siglo XX: 1900-1999
        this.startDate = new Date(1900, 0, 1);  // 01/01/1900
        this.endDate = new Date(1999, 11, 31);  // 31/12/1999
        // console.log('📅 Rango rápido Siglo XX:', this.startDate.toString(), '-', this.endDate.toString());
        break;

      case 'siglo21':
        // Siglo XXI: 2000-Hoy (crear fecha LOCAL sin hora)
        this.startDate = new Date(2000, 0, 1);  // 01/01/2000
        this.endDate = new Date(currentYear, currentMonth, currentDay);  // Hoy (sin hora)
        // console.log('📅 Rango rápido Siglo XXI:', this.startDate.toString(), '-', this.endDate.toString());
        break;

      case 'bicentenario':
        // Bicentenario: 1825 hasta hoy (desde el inicio del archivo histórico)
        this.startDate = new Date(1825, 0, 1);  // 01/01/1825
        this.endDate = new Date(currentYear, currentMonth, currentDay);  // Hoy (sin hora)
        // console.log('📅 Rango rápido Bicentenario:', this.startDate.toString(), '-', this.endDate.toString());
        break;
    }

    // Actualizar los selectores de mes/año para mostrar el rango seleccionado
    this.startMonth = this.startDate.getMonth();
    this.startYear = this.startDate.getFullYear();
    this.endMonth = this.endDate.getMonth();
    this.endYear = this.endDate.getFullYear();

    // Regenerar calendarios para reflejar las nuevas fechas
    this.generateStartCalendar();
    this.generateEndCalendar();

    // console.log('✅ Rango rápido aplicado correctamente');
  }

  clearDates(): void {
    this.startDate = null;
    this.endDate = null;
    this.activeTab = 'start'; // Volver al primer tab
    this.activeQuickRange = null; // Limpiar el botón activo
  }

  applyDates(): void {
    if (this.startDate && this.endDate) {
      // console.log('📤 Emitiendo rango de fechas:', {
      //   start: this.startDate.toString(),
      //   end: this.endDate.toString()
      // });

      this.dateRangeSelected.emit({
        start: this.startDate,
        end: this.endDate
      });
      this.closeModal();
    }
  }

  // Métodos para incrementar/decrementar años
  incrementStartYear(): void {
    if (this.startYear < this.maxDate.getFullYear()) {
      this.startYear++;
      this.onStartMonthYearChange();
    }
  }

  decrementStartYear(): void {
    if (this.startYear > this.minDate.getFullYear()) {
      this.startYear--;
      this.onStartMonthYearChange();
    }
  }

  incrementEndYear(): void {
    if (this.endYear < this.maxDate.getFullYear()) {
      this.endYear++;
      this.onEndMonthYearChange();
    }
  }

  decrementEndYear(): void {
    if (this.endYear > this.minDate.getFullYear()) {
      this.endYear--;
      this.onEndMonthYearChange();
    }
  }

  // Métodos para manejar el focus en inputs de año
  onStartYearFocus(event: any): void {
    // Guardar el valor actual antes de editar
    this.previousStartYear = this.startYear;
    // Seleccionar todo el texto para facilitar el reemplazo
    event.target.select();
  }

  onEndYearFocus(event: any): void {
    // Guardar el valor actual antes de editar
    this.previousEndYear = this.endYear;
    // Seleccionar todo el texto para facilitar el reemplazo
    event.target.select();
  }

  // Métodos para validar años
  validateStartYear(): void {
    const minYear = this.minDate.getFullYear();
    const maxYear = this.maxDate.getFullYear();

    // Si el input está vacío o es inválido, restaurar el valor anterior
    if (!this.startYear || isNaN(this.startYear)) {
      this.startYear = this.previousStartYear;
      this.onStartMonthYearChange();
      return;
    }

    // Validar rango
    if (this.startYear < minYear) {
      this.startYear = minYear;
    } else if (this.startYear > maxYear) {
      this.startYear = maxYear;
    }

    // Actualizar el valor previo con el valor validado
    this.previousStartYear = this.startYear;
    this.onStartMonthYearChange();
  }

  validateEndYear(): void {
    const minYear = this.minDate.getFullYear();
    const maxYear = this.maxDate.getFullYear();

    // Si el input está vacío o es inválido, restaurar el valor anterior
    if (!this.endYear || isNaN(this.endYear)) {
      this.endYear = this.previousEndYear;
      this.onEndMonthYearChange();
      return;
    }

    // Validar rango
    if (this.endYear < minYear) {
      this.endYear = minYear;
    } else if (this.endYear > maxYear) {
      this.endYear = maxYear;
    }

    // Actualizar el valor previo con el valor validado
    this.previousEndYear = this.endYear;
    this.onEndMonthYearChange();
  }

  closeModal(): void {
    this.closeEvent.emit();
  }
}
